import { useState, useEffect, useCallback } from 'react'
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from './ui/table'

const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000'

/* Event → badge colour (mirrors the header indicator semantics). */
const EVENT_COLOR = {
  connect:           { bg: '#10B98115', fg: '#047857' },
  reconciled:        { bg: '#10B98115', fg: '#047857' },
  reconnect_attempt: { bg: '#F59E0B1a', fg: '#92400e' },
  disconnect:        { bg: '#EF44441a', fg: '#b91c1c' },
}

const LIMIT = 100

/**
 * Admin-only IBKR connectivity audit log (MVP-1 Block D.3).
 * Reads GET /api/connection/events; optional user_id filter + offset paging.
 */
export default function ConnectionLog() {
  const [events,  setEvents]  = useState([])
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)
  const [offset,  setOffset]  = useState(0)
  const [userId,  setUserId]  = useState('')        // raw input
  const [userFilter, setUserFilter] = useState('')  // applied filter

  const load = useCallback(() => {
    const token = localStorage.getItem('token')
    setLoading(true)
    setError(null)
    const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) })
    if (userFilter) params.set('user_id', userFilter)
    fetch(`${BASE}/api/connection/events?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 403) throw new Error('Доступ лише для адміністратора')
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(d => setEvents(d.events || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [offset, userFilter])

  useEffect(() => { load() }, [load])

  const applyFilter = () => { setOffset(0); setUserFilter(userId.trim()) }
  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('uk-UA') : '—')

  const ctrlBtn = {
    padding: '8px 14px', borderRadius: 8, border: '1px solid #E5E7EB',
    background: '#fff', cursor: 'pointer', fontSize: 13, color: '#101828',
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>🔌 IBKR Connection Log</h1>
        <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
          Аудит підключень ботів до IBKR (connect / disconnect / reconnect / reconciled)
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="number"
          value={userId}
          onChange={e => setUserId(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') applyFilter() }}
          placeholder="Фільтр по user_id"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, width: 160, outline: 'none' }}
        />
        <button onClick={applyFilter} style={ctrlBtn}>Застосувати</button>
        {userFilter && (
          <button onClick={() => { setUserId(''); setUserFilter(''); setOffset(0) }} style={ctrlBtn}>✕ Скинути</button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={load} style={ctrlBtn}>↻ Оновити</button>
        <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} style={{ ...ctrlBtn, opacity: offset === 0 ? 0.5 : 1, cursor: offset === 0 ? 'not-allowed' : 'pointer' }}>← Новіші</button>
        <button disabled={events.length < LIMIT} onClick={() => setOffset(offset + LIMIT)} style={{ ...ctrlBtn, opacity: events.length < LIMIT ? 0.5 : 1, cursor: events.length < LIMIT ? 'not-allowed' : 'pointer' }}>Старіші →</button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, overflow: 'hidden' }}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Час</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Компонент</TableHead>
              <TableHead>Подія</TableHead>
              <TableHead>Деталі</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map(ev => {
              const c = EVENT_COLOR[ev.event] || { bg: '#F3F4F6', fg: '#374151' }
              return (
                <TableRow key={ev.id}>
                  <TableCell className="whitespace-nowrap text-[#64748b]">{fmtTime(ev.occurred_at)}</TableCell>
                  <TableCell>{ev.user_id ?? '—'}</TableCell>
                  <TableCell className="text-[#64748b]">{ev.component}</TableCell>
                  <TableCell>
                    <span style={{ background: c.bg, color: c.fg, borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                      {ev.event}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-[#64748b]">
                    {ev.detail && Object.keys(ev.detail).length ? JSON.stringify(ev.detail) : '—'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>

        {!loading && events.length === 0 && !error && (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            Подій не знайдено
          </div>
        )}
        {loading && (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Завантаження…</div>
        )}
      </div>
    </div>
  )
}
